/**
 * ModernizeIT API Adapter
 * Real REST client for the ModernizeIT API layer
 *
 * API Base: http://localhost:8000
 */

import { createModernizeAPIPort } from '../../core/ports'

// Default config
const DEFAULT_CONFIG = {
  baseUrl: 'http://localhost:8000',
  timeout: 300000, // 5 minutes - analysis jobs can take a while
}

/**
 * Create a ModernizeIT API adapter
 * @param {Object} config
 * @param {string} config.baseUrl - API base URL
 * @param {number} config.timeout - Request timeout in ms
 */
export function createModernizeAPIAdapter(config = {}) {
  const { baseUrl, timeout } = { ...DEFAULT_CONFIG, ...config }

  // Helper: Make API request
  const request = async (method, endpoint, body = null, options = {}) => {
    const url = `${baseUrl}${endpoint}`
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      // Add auth token if available
      const token = localStorage.getItem('modernizeit_token')
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const fetchOptions = {
        method,
        headers,
        signal: controller.signal,
      }

      if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body)
      }

      const response = await fetch(url, fetchOptions)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        // Handle FastAPI validation errors (array of objects)
        let errorMessage = `API Error: ${response.status} ${response.statusText}`
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Validation errors: [{loc: [...], msg: "...", type: "..."}]
            errorMessage = errorData.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('; ')
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          }
        }
        throw new Error(errorMessage)
      }

      // Handle different response types
      if (options.responseType === 'blob') {
        return response.blob()
      }
      if (options.responseType === 'text') {
        return response.text()
      }

      return response.json()
    } finally {
      clearTimeout(timeoutId)
    }
  }

  // Helper: GET request
  const get = (endpoint, options) => request('GET', endpoint, null, options)

  // Helper: POST request
  const post = (endpoint, body, options) => request('POST', endpoint, body, options)

  // Implementation
  const implementation = {
    // ===== Health =====

    healthCheck: async () => {
      return get('/health')
    },

    // ===== Ingest =====

    startIngest: async ({
      scoutAccountId,
      applicationName,
      sourcePath,
      sourceLanguage = 'cobol',
      targetLanguage = 'java',
      generateTypeMappings = true,
    }) => {
      return post('/ingest/upload', {
        scoutAccountId,
        applicationName,
        sourcePath,
        sourceLanguage,
        targetLanguage,
        generateTypeMappings,
      })
    },

    getIngestStatus: async (jobId) => {
      return get(`/ingest/jobs/${jobId}/status`)
    },

    getIngestResults: async (jobId) => {
      return get(`/ingest/results/${jobId}`)
    },

    // ===== Code Analysis =====

    startCodeAnalysis: async ({
      scoutAccountId,
      applicationName,
      userId,
      sourceHash,
      mainProgram,
      generateJava = true,
      generateGraphs = true,
    }) => {
      return post('/codeanalysis', {
        scout_account_id: scoutAccountId,
        application_name: applicationName,
        user_id: userId,
        source_hash: sourceHash,
        main_program: mainProgram,
        generate_java: generateJava,
        generate_graphs: generateGraphs,
      })
    },

    getCodeAnalysisStatus: async (jobId) => {
      return get(`/codeanalysis/${jobId}/status`)
    },

    getCodeAnalysisResults: async (jobId) => {
      return get(`/codeanalysis/${jobId}/results`)
    },

    getCodeAnalysisArtifact: async (jobId, filename) => {
      return get(`/codeanalysis/${jobId}/results/json/${filename}`)
    },

    getCodeAnalysisGraph: async (jobId, filename) => {
      return get(`/codeanalysis/${jobId}/results/graphs/${filename}`, { responseType: 'blob' })
    },

    // ===== Code Refactor =====

    startCodeRefactor: async ({
      scoutAccountId,
      applicationName,
      className,
      mode = 'analyze',
      useAI = true,
      recipesToApply,
    }) => {
      return post('/coderefactor', {
        scoutAccountId,
        applicationName,
        className,
        mode,
        useAI,
        recipesToApply,
      })
    },

    transformCode: async ({
      scoutAccountId,
      applicationName,
      recipesToApply = 'all',
    }) => {
      return post('/coderefactor/transform', {
        scoutAccountId,
        applicationName,
        recipesToApply,
      })
    },

    getCodeRefactorStatus: async (jobId) => {
      return get(`/coderefactor/${jobId}/status`)
    },

    getCodeRefactorResults: async (jobId) => {
      return get(`/coderefactor/${jobId}/results`)
    },

    getCodeRefactorRecipes: async (jobId) => {
      return get(`/coderefactor/${jobId}/results/recipes`)
    },

    // ===== Dependency Mapper =====

    startDependencyMapper: async ({
      scoutAccountId,
      applicationName,
      userId,
      sourceType,
    }) => {
      return post('/dependencymapper', {
        scout_account_id: scoutAccountId,
        application_name: applicationName,
        user_id: userId,
        source_type: sourceType,
      })
    },

    compareDependencies: async ({
      scoutAccountId,
      applicationName,
    }) => {
      return post('/dependencymapper/compare', {
        scoutAccountId,
        applicationName,
      })
    },

    getDependencyMapperStatus: async (jobId) => {
      return get(`/dependencymapper/${jobId}/status`)
    },

    getDependencyMapperResults: async (jobId) => {
      return get(`/dependencymapper/${jobId}/results`)
    },

    // ===== Monolith Identifier =====

    startMonolithIdentifier: async ({
      scoutAccountId,
      applicationName,
      sourceType,
    }) => {
      return post('/monolithidentifier', {
        scoutAccountId,
        applicationName,
        sourceType,
      })
    },

    compareMonolithPatterns: async ({
      scoutAccountId,
      applicationName,
    }) => {
      return post('/monolithidentifier/compare', {
        scoutAccountId,
        applicationName,
      })
    },

    getMonolithIdentifierStatus: async (jobId) => {
      return get(`/monolithidentifier/${jobId}/status`)
    },

    getMonolithIdentifierResults: async (jobId) => {
      return get(`/monolithidentifier/${jobId}/results`)
    },

    // ===== Data Analysis =====

    startDataAnalysis: async ({
      scoutAccountId,
      applicationName,
      userId,
    }) => {
      return post('/dataanalysis', {
        scout_account_id: scoutAccountId,
        application_name: applicationName,
        user_id: userId,
      })
    },

    getDataAnalysisStatus: async (jobId) => {
      return get(`/dataanalysis/${jobId}/status`)
    },

    getDataAnalysisResults: async (jobId) => {
      return get(`/dataanalysis/${jobId}/results`)
    },

    getERD: async (jobId) => {
      return get(`/dataanalysis/${jobId}/results/erd`)
    },

    getDataLineage: async (jobId) => {
      return get(`/dataanalysis/${jobId}/results/lineage`)
    },

    // ===== Discovery =====

    startDiscovery: async ({
      scoutAccountId,
      applicationName,
      enableAI = true,
      currentMips,
      annualMainframeCost,
      cobolDeveloperCount,
      cobolDeveloperSalary,
    }) => {
      return post('/discovery/analyze', {
        scoutAccountId,
        applicationName,
        enableAI,
        currentMips,
        annualMainframeCost,
        cobolDeveloperCount,
        cobolDeveloperSalary,
      })
    },

    calculateROI: async (params) => {
      return post('/discovery/roi/calculate', params)
    },

    getDiscoveryResults: async (jobId, section) => {
      const endpoint = section
        ? `/discovery/results/${jobId}/${section}`
        : `/discovery/results/${jobId}`
      return get(endpoint)
    },

    getROIAssumptions: async () => {
      return get('/discovery/assumptions')
    },

    // ===== Architecture =====

    startArchitecture: async ({
      scoutAccountId,
      applicationName,
      discoveryJobId,
      dataAnalysisJobId,
      codeAnalysisJobId,
      codeRefactorJobId,
      generateIaC = true,
    }) => {
      return post('/architecture/analyze', {
        scoutAccountId,
        applicationName,
        discoveryJobId,
        dataAnalysisJobId,
        codeAnalysisJobId,
        codeRefactorJobId,
        generateIaC,
      })
    },

    getArchitectureStatus: async (jobId) => {
      return get(`/architecture/${jobId}/status`)
    },

    getArchitectureResults: async (jobId, section) => {
      const endpoint = section
        ? `/architecture/${jobId}/results/${section}`
        : `/architecture/${jobId}/results`
      return get(endpoint)
    },

    getIaCTemplates: async (jobId) => {
      return get(`/architecture/${jobId}/results/iac`)
    },

    getIaCTemplateFile: async (jobId, filename) => {
      return get(`/architecture/${jobId}/results/iac/${filename}`, { responseType: 'text' })
    },

    // ===== Service Generation =====

    startServiceGeneration: async ({
      scoutAccountId,
      applicationName,
      userId,  // ✅ ADD userId parameter
      programName,
      basePackage,
      includeAllRepositories = true,
      includeAllFields = true,
      strictValidation = true,
    }) => {
      return post('/servicegeneration', {
        scout_account_id: scoutAccountId,
        application_name: applicationName,
        user_id: userId,  // ✅ ADD user_id to request body
        program_name: programName,
        base_package: basePackage,
        include_all_repositories: includeAllRepositories,
        include_all_fields: includeAllFields,
        strict_validation: strictValidation,
      })
    },

    getServiceGenerationStatus: async (jobId) => {
      return get(`/servicegeneration/${jobId}`)
    },

    getServiceGenerationResults: async (jobId) => {
      return get(`/servicegeneration/${jobId}/results`)
    },

    downloadServiceGeneration: async (jobId) => {
      return get(`/servicegeneration/${jobId}/download`, { responseType: 'blob' })
    },

    // ===== Generic Job Helpers =====

    getJobStatus: async (jobType, jobId) => {
      return get(`/${jobType}/${jobId}/status`)
    },

    getJobResults: async (jobType, jobId) => {
      return get(`/${jobType}/${jobId}/results`)
    },

    getJobArtifact: async (jobType, jobId, filename) => {
      return get(`/${jobType}/${jobId}/results/json/${filename}`)
    },
  }

  return createModernizeAPIPort(implementation)
}

export default createModernizeAPIAdapter
